import { createSelector } from '@reduxjs/toolkit';

import { EMPTY_ARRAY } from 'lib/constants';
import {
  GET_MEAL_SELLOUTS_V3_RESPONSE,
  GET_VIEWABLE_BASKETS_V3_RESPONSE,
  GET_MENU_MEALS_V3_RESPONSE,
  GET_BASKET_MENU_MEAL_V3_RESPONSE,
  GET_MEAL_BADGES_V3_RESPONSE,
} from 'lib/constants/v3Actions';

export const getStageStepKey = stageStep => {
  return `stageStep${stageStep}`;
};

const initialState = {};

const initializeMenuMeals = (currentMenusData, menuSlugs) => {
  const menusHash = menuSlugs.reduce((hash, slug) => ({ ...hash, [slug]: {} }), {});

  return {
    ...menusHash,
    ...currentMenusData,
  };
};

const getMealDetailsStageKey = meal => {
  if (meal.aLaCarte) {
    return getStageStepKey(2);
  } else {
    return getStageStepKey(1);
  }
};

const applyBasketMenuMealResponse = (currentMenusData, menuSlug, updatedMeal) => {
  const fullyLoadedMeal = {
    ...updatedMeal,
    isFullyLoaded: true,
  };

  const menuMeals = currentMenusData[menuSlug] || {};
  const updatedMenuData = { ...menuMeals };

  for (const [stageStepKey, stageStepMeals] of Object.entries(menuMeals)) {
    if (!Array.isArray(stageStepMeals)) continue;

    const mealExistsInStage = stageStepMeals.some(meal => meal.id === fullyLoadedMeal.id);

    if (mealExistsInStage) {
      return {
        ...currentMenusData,
        [menuSlug]: {
          ...updatedMenuData,
          [stageStepKey]: stageStepMeals.map(meal =>
            meal.id === fullyLoadedMeal.id
              ? {
                  ...meal,
                  ...fullyLoadedMeal,
                }
              : meal
          ),
        },
      };
    }
  }

  const stageStepKey = getMealDetailsStageKey(fullyLoadedMeal);
  const stageStepMeals = updatedMenuData[stageStepKey] || [];

  return {
    ...currentMenusData,
    [menuSlug]: {
      ...updatedMenuData,
      [stageStepKey]: [...stageStepMeals, fullyLoadedMeal],
    },
  };
};

const applyMealBadgesToMenuMeals = (currentMenusData, menuSlug, mealBadgeDataIndexedByMealId) => {
  const menuMeals = currentMenusData[menuSlug];

  if (!menuMeals) {
    return currentMenusData;
  }

  const updatedMenuData = {};

  for (const [stage, stageMeals] of Object.entries(menuMeals)) {
    updatedMenuData[stage] = stageMeals.map(stageMeal => {
      const matchingMeal = mealBadgeDataIndexedByMealId[stageMeal.id];

      if (!matchingMeal) return { ...stageMeal, mealBadge: null };

      return { ...stageMeal, ...matchingMeal };
    });
  }

  return {
    ...currentMenusData,
    [menuSlug]: updatedMenuData,
  };
};

const applySelloutsToMenuMeals = (currentMenusData, menuSlug, mealSellouts) => {
  const menuMeals = currentMenusData[menuSlug];

  if (!menuMeals) {
    return currentMenusData;
  }

  const updatedMenuData = Object.fromEntries(
    Object.entries(menuMeals).map(([stageStepKey, stageStepMeals]) => [
      stageStepKey,
      stageStepMeals.reduce((acc, meal) => {
        // reject meals that have hidden sellout
        if (mealSellouts[meal.id]?.hidden) {
          return acc;
        }

        // Apply sellout data if it exists, otherwise reset sellout status to handle previously sold out meals
        if (mealSellouts[meal.id]) {
          const { soldOut, selloutDisplayCopy } = mealSellouts[meal.id];
          return [...acc, { ...meal, soldOut, selloutDisplayCopy }];
        }
        return [...acc, { ...meal, soldOut: false, selloutDisplayCopy: null }];
      }, []),
    ])
  );

  return {
    ...currentMenusData,
    [menuSlug]: updatedMenuData,
  };
};

export default (state = initialState, action) => {
  switch (action.type) {
    case GET_VIEWABLE_BASKETS_V3_RESPONSE: {
      const basketMenuSlugs = action.response.weeklyBaskets.map(wb => wb.menuSlug);
      const missingMenuSlugs = action.response.missingBasketMenus.map(mbm => mbm.menuSlug);

      return initializeMenuMeals(state, basketMenuSlugs.concat(missingMenuSlugs));
    }
    case GET_MENU_MEALS_V3_RESPONSE:
      return {
        ...state,
        [action.params.menuSlug]: {
          ...state[action.params.menuSlug],
          ...action.response.meals,
        },
      };
    case GET_MEAL_SELLOUTS_V3_RESPONSE:
      return applySelloutsToMenuMeals(
        state,
        action.params.menuSlug,
        action.response.mealSellouts || []
      );
    case GET_MEAL_BADGES_V3_RESPONSE:
      return applyMealBadgesToMenuMeals(state, action.params.menuSlug, action.response);
    case GET_BASKET_MENU_MEAL_V3_RESPONSE:
      return applyBasketMenuMealResponse(state, action.params.menuSlug, action.response.meal);
    default:
      return state;
  }
};

export const selectAllMenuMeals = (state, menuSlug) =>
  Object.values(state[menuSlug] || {})
    .filter(Array.isArray)
    .flat();

const getMenuMeals = (state, menuSlug) => state[menuSlug];
const getStageStep = (state, menuSlug, stageStep) => stageStep;

export const selectMenuMeals = createSelector(
  [getMenuMeals, getStageStep],
  (menuMeals, stageStep) => {
    if (!menuMeals) return EMPTY_ARRAY;

    if (stageStep) {
      return menuMeals[getStageStepKey(stageStep)] || EMPTY_ARRAY;
    }

    return Object.values(menuMeals).flat();
  }
);

export const selectNonDonationMealsForMenu = createSelector([selectMenuMeals], meals =>
  meals.filter(meal => !meal.isDonation)
);

export const selectMealAddons = (state, menuSlug, stageStep) => {
  return selectMenuMeals(state, menuSlug, stageStep).reduce((acc, meal) => {
    if (meal.mealAddonId) {
      acc[meal.id] = {
        id: meal.mealAddonId,
        name: meal.mealAddonName,
        displayName: meal.mealAddonDisplayName,
        pluralizedDisplayName: meal.mealAddonPluralizedDisplayName,
        maxSelectionQuantity: meal.mealAddonMaxSelectionQuantity,
      };
    }
    return acc;
  }, {});
};

export const selectOptIns = (state, menuSlug, stageStep) => {
  return selectMenuMeals(state, menuSlug, stageStep).reduce((acc, meal) => {
    if (Boolean(meal.optIns) && Object.keys(meal.optIns).length > 0) {
      acc[meal.id] = meal.optIns;
    }
    return acc;
  }, {});
};
